import { z } from 'zod';

// STE charge modes as shown in the UI. evcc >= 0.317 only knows off/now/smart
// plus "alwaysCharge"; see docs/evcc-api-mapping.md §3.1.
export const ChargeMode = z.enum(['off', 'pv', 'minpv', 'now']);
export type ChargeMode = z.infer<typeof ChargeMode>;

// Heat pumps: "pv" = boost on PV surplus (evcc smart mode). Dimming is never user-selectable.
export const HeatPumpMode = z.enum(['off', 'pv', 'now']);
export type HeatPumpMode = z.infer<typeof HeatPumpMode>;

export type EvccMode = 'off' | 'now' | 'smart';
export type EvccAlwaysCharge = 'off' | 'on' | 'once';

export interface EvccModeSetting {
  mode: EvccMode;
  /** undefined: leave alwaysCharge unchanged */
  alwaysCharge?: EvccAlwaysCharge;
}

export function toEvccMode(mode: ChargeMode | HeatPumpMode): EvccModeSetting {
  switch (mode) {
    case 'off':
      return { mode: 'off' };
    case 'now':
      return { mode: 'now' };
    case 'pv':
      return { mode: 'smart', alwaysCharge: 'off' };
    case 'minpv':
      return { mode: 'smart', alwaysCharge: 'on' };
  }
}

export function fromEvccMode(
  mode: string | undefined,
  alwaysCharge: string | undefined,
): ChargeMode | undefined {
  switch (mode) {
    case 'off':
    case 'now':
      return mode;
    case 'smart':
      return alwaysCharge === 'on' || alwaysCharge === 'once' ? 'minpv' : 'pv';
    // legacy values, normalized by evcc on input
    case 'pv':
      return 'pv';
    case 'minpv':
      return 'minpv';
    default:
      return undefined;
  }
}

export function fromEvccHeatPumpMode(mode: string | undefined): HeatPumpMode | undefined {
  const result = fromEvccMode(mode, 'off');
  return result === 'minpv' ? 'pv' : result;
}
